import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { resetState, setOrderByDirection, setOrderByProperty, handleGetWorkflowRequestsData, getModalSwitch, resetScroll } from '@app/store/actions';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import { getMenuItemById, infiniteScrollItemsFetchLimit, created, genericErrorToast } from '@app/helpers';
import {
    ITableHead,
    IListPageBarEnabled,
    IActiveFilter,
    IFabMenu,
    FilterEnum,
    IWorkflowRequest,
    IFilterSelect,
    IDotMenu,
    OrderType,
    Order
} from '@app/models';
import { TableCell, Link } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { hasOrgPermission } from '@app/helpers/permissions';
import { ManageSystem } from '@app/store/constants';
import { FabMenu, IModalData, ModalComponent, PasslyTable } from '@app/components';
import { confirm } from '@app/components/Modal/confirmModal';
import { workflowRequestService } from '@app/services';
import UploadWorkflowDialog from '@app/pages/ssoManager/dialogs/uploadWorkflowFile.dialog';
import ValidateWorkflowDialog from '@app/pages/ssoManager/dialogs/validateWorkflow.dialog';

interface IWorkflowRequestsTableData {
    checked: boolean;
    cantEdit?: boolean;
    requestUrl: string;
    requestState: string;
    createdAppName: string;
    createdAppId: string;
    created: Date;
    iconUrl: string;
    id: string;
}

enum TableHeadEnum {
    requestUrl = 'url',
    requestState = 'requestState',
    createdAppName = 'createdApplicationName',
    created = 'created'
}

const WorkflowRequestPage = () => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.WorkflowRequests);

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('Url');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IWorkflowRequestsTableData[]>([]);
    const [noPermsToView, setNoPermsToView] = useState(false);

    const workflowRequests = useSelector((state: any) => state.workflowRequests.workflowRequests);
    const workflowRequestsCount = useSelector((state: any) => state.workflowRequests.count);
    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: true
    };

    const openValidateDialog = (id: string) => () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('TEST_WORKFLOW_HEADER'),
            modalSwitcher: modalSwitchState,
            form: <ValidateWorkflowDialog id={id} handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const getComplexRequestUrlCell = (row: IWorkflowRequestsTableData) => {
        return (
            <TableCell>
                {row.requestState === created ? (
                    <Link component='button' onClick={openValidateDialog(row.id)}>
                        {row.requestUrl}
                    </Link>
                ) : (
                    <div>{row.requestUrl}</div>
                )}
            </TableCell>
        );
    };
    const getComplexCreatedAppNameCell = (row: IWorkflowRequestsTableData) => {
        return <TableCell>{row.createdAppName ? <div>{row.createdAppName}</div> : <div>{translation('REQUEST_NO_APP_NAME')}</div>}</TableCell>;
    };

    const requestStateSelectData: IFilterSelect[] = [
        {
            id: 0,
            label: 'REQUEST_STATE_EVERYTHING',
            value: 'Everything'
        },
        {
            id: 1,
            label: 'REQUEST_STATE_CREATED',
            value: 'Created'
        },
        {
            id: 4,
            label: 'REQUEST_STATE_VALIDATED_FAILED',
            value: 'Failed'
        },
        {
            id: 2,
            label: 'REQUEST_STATE_REQUESTED',
            value: 'Requested'
        },
        {
            id: 3,
            label: 'REQUEST_STATE_VALIDATED_PASSED',
            value: 'Validated'
        }
    ];

    // Only sortable on display name for apps, handled in return() function
    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.requestUrl,
            label: 'REQUEST_URL',
            type: 'getComplexCell',
            getComplexCell: getComplexRequestUrlCell,
        },
        {
            id: TableHeadEnum.requestState,
            label: 'REQUEST_STATE',
            filterType: FilterEnum.state,
            suggestionsForFilter: requestStateSelectData
        },
        {
            id: TableHeadEnum.createdAppName,
            label: 'REQUEST_MAPPED_APP',
            type: 'getComplexCell',
            getComplexCell: getComplexCreatedAppNameCell,
            hideSorting: true
        },
        {
            id: TableHeadEnum.created,
            label: 'REQUEST_CREATED',
            type: 'date'
        }, {
            id: TableHeadEnum.requestUrl,
            label: 'REQUEST_SITE_CONTAINS',
            type: 'getComplexCell',
            getComplexCell: getComplexRequestUrlCell,
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.requestUrl,
            hidden: true
        }
    ];

    const menu: IFabMenu = {
        name: 'APP_WORKFLOW_REQUESTS',
        actions: [
            {
                name: 'SAMLX_SUBMISSION_TOOLTIP',
                method: () => uploadSamlx(),
                iconElement: <CloudUploadIcon />
            }
        ]
    };

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        setNoPermsToView(!hasOrgPermission([ManageSystem]));
    }, [organizationFilter]);

    useEffect(() => {
        if (!loadingData && workflowRequests) {
            let newRows = workflowRequests.map((workflowRequest: IWorkflowRequest) => {
                const data: IWorkflowRequestsTableData = {
                    checked: false,
                    cantEdit: workflowRequest.State === created,
                    requestUrl: workflowRequest.Url,
                    requestState: workflowRequest.State,
                    createdAppName: workflowRequest.CreatedApplicationName,
                    createdAppId: workflowRequest.CreatedApplicationId,
                    created: workflowRequest.Created,
                    iconUrl: workflowRequest.IconUrl,
                    id: workflowRequest.Id
                };
                return data;
            });

            if (workflowRequests.length === 0) {
                setRows([]);
            } else {
                setRows(rows.concat(newRows));
            }
        }
    }, [loadingData, workflowRequests]);

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        dispatch(handleGetWorkflowRequestsData(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, organizationFilter, isReset, activeFilters));
    };

    const uploadSamlx = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('SAMLX_SUBMISSION_TITLE'),
            modalSwitcher: modalSwitchState,
            form: <UploadWorkflowDialog handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deleteRequestsAreYouSure(ids);
    };

    const deleteSelected = useCallback(() => {
        // loop thru rows.checked
        let ids: string[] = [];

        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        deleteRequestsAreYouSure(ids);
    }, [rows]);

    const deleteRequestsAreYouSure = (ids: any) => {
        confirm(translation('DELETE_REQUEST_BODY'), translation('DELETE'), translation('CANCEL'), translation('DELETE_REQUEST_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                deleteRequests(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteRequests = (ids: string[]) => {
        if (ids.length > 0) {
            let requests: any = {
                Requests: ids
            };

            workflowRequestService.deleteWorkflowRequest(requests, genericErrorToast, handleDeleteSuccess);
        }
    };

    const handleDeleteSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const validateRow = (e: any) => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('TEST_WORKFLOW_HEADER'),
            modalSwitcher: modalSwitchState,
            form: <ValidateWorkflowDialog id={e.currentTarget.dataset.id} handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    return (
        <>
            <ModalComponent {...modalData} />

            {noPermsToView ? (
                <div className='table--empty'>{translation('NO_ORG_APP_PERMISSIONS')}</div>
            ) : (
                <>
                    <PasslyTable
                        rows={rows}
                        setRows={setRows}
                        order={order}
                        orderBy={orderBy}
                        handleOrderChange={createSortHandler}
                        headerItems={headCells}
                        rowsCount={workflowRequestsCount}
                        onFetch={fetchMoreData}
                        withCheckboxes={hasOrgPermission([ManageSystem])}
                        actionMenuItems={actionMenuItems}
                        emptyListHelper={translation('EMPTY_REQUEST_LIST')}
                        loadingData={loadingData}
                        editRow={hasOrgPermission([ManageSystem]) ? validateRow : undefined}
                        deleteRow={hasOrgPermission([ManageSystem]) ? deleteRow : undefined}
                    />

                    <FabMenu menu={menu} />
                </>
            )}
        </>
    );
};

export default WorkflowRequestPage;
